import type { RecipeStore } from '../store/types.js';
import { wirePanelRowEvents } from './panelRowEvents.js';
import { PreparationDragController } from './PreparationDragController.js';

/**
 * PreparationView — standalone view rendering the currently selected
 * recipe's preparation steps, independent of GraphVisualizer/ListView.
 * See IngredientsView's doc comment (its exact counterpart) for the full
 * rationale — this class is identical except for the row selector and the
 * HTML-building function it's handed, plus the drag-to-reorder wiring
 * IngredientsView has no equivalent of (see PreparationDragController.ts).
 */
export class PreparationView {
  private containers: HTMLElement[];
  private store: RecipeStore;
  private buildHtml: (recipeId: string | null) => string;
  private onCompletePickNode?: (nodeId: string) => void;
  private dragController: PreparationDragController;
  // See IngredientsView's identical field for why this memoization exists
  // — lastEditMode alongside it because the memo check alone would
  // otherwise miss re-wiring drag handlers in the (currently
  // hypothetical) case of an edit-mode toggle that doesn't also change
  // buildPreparationHtml()'s own edit-mode-conditional output (the drag
  // handle) in a way that'd already change `html` on its own.
  private lastHtml: string | null = null;
  private lastEditMode: boolean | null = null;

  constructor(
    containers: HTMLElement | HTMLElement[],
    store: RecipeStore,
    buildHtml: (recipeId: string | null) => string,
    onCompletePickNode?: (nodeId: string) => void,
  ) {
    this.containers = Array.isArray(containers) ? containers : [containers];
    this.store = store;
    this.buildHtml = buildHtml;
    this.onCompletePickNode = onCompletePickNode;

    this.dragController = new PreparationDragController({
      // Fired unconditionally on both start and end — same split
      // ListView/ListDragController use: the store subscription below
      // skips re-rendering only while a drag is actually active, so a
      // notify arriving mid-drag (e.g. from hovering a row, which
      // wirePanelRowEvents already wires to store.setHoveredNode())
      // can't blow away the drag's own ghost/insertion-line DOM out from
      // under it; this explicit call is what catches up once it ends.
      onDragStateChange: () => this.render(),
      previewOrder: (nodeId, anchorNodeId, insertBefore) => {
        const targetIndex = this.store.resolveStepOrderIndexRelativeTo(nodeId, anchorNodeId, insertBefore);
        return this.store.previewStepOrderCascaded(nodeId, targetIndex);
      },
      onDrop: (nodeId, anchorNodeId, insertBefore) => {
        const targetIndex = this.store.resolveStepOrderIndexRelativeTo(nodeId, anchorNodeId, insertBefore);
        this.store.moveNodeToStepOrder(nodeId, targetIndex);
      },
    });

    this.store.subscribe(() => {
      if (!this.dragController.isActive()) this.render();
    });
    this.render();
  }

  private render(): void {
    const html = this.buildHtml(this.store.getSelectedRecipeId());
    const editMode = this.store.isEditMode();
    if (html === this.lastHtml && editMode === this.lastEditMode) return;
    this.lastHtml = html;
    this.lastEditMode = editMode;
    for (const container of this.containers) {
      container.innerHTML = html;
      wirePanelRowEvents(container, this.store, this.onCompletePickNode);
      // Dragging to reorder is an edit-mode action, same as every other
      // real mutation (reparenting, deleting, ...) — not something
      // offered while just browsing.
      if (editMode) {
        const rows = container.querySelectorAll<HTMLElement>('.preparation-step-row');
        for (const row of Array.from(rows)) {
          const nodeId = row.dataset.nodeId;
          if (!nodeId) continue;
          const handle = row.querySelector<HTMLElement>('.preparation-step-drag-handle');
          if (!handle) continue; // buildPreparationHtml() only renders one in edit mode — should always be present here
          this.dragController.registerRow(row, handle, nodeId);
        }
      }
    }
  }
}
